export type Severity = "P1" | "P2" | "P3" | "P4";
export type IncidentStatus = "open" | "resolved" | "needs_human";
export type RunMode = "dry_run" | "live";
export type RunStatus =
  | "dry_run"
  | "success"
  | "failure"
  | "skipped"
  | "rolled_back";

export interface User {
  id: string;
  username: string;
  role: string;
}

// CLI 43 — 2FA TOTP.
export interface LoginResponse {
  requires_2fa: boolean;
  requires_2fa_setup: boolean;
}

export interface TotpStatusResponse {
  enabled: boolean;
  enabled_at: string | null;
  last_used_at: string | null;
  backup_codes_remaining: number;
}

export interface TotpSetupResponse {
  qr_data_uri: string;
  backup_codes: string[];
}

export interface TotpBackupCodesResponse {
  backup_codes: string[];
}

export interface IncidentSummary {
  incident_id: string;
  priority: Severity;
  status: IncidentStatus;
  correlation_dimension: string;
  correlation_key: string;
  summary: string;
  event_count: number;
  affected_hosts: string[];
  trigger_descriptions: string[];
  metric_names: string[];
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  resolution_reason: string | null;
  has_playbook_run: boolean;
  last_playbook_run_status: string | null;
}

export interface IncidentList {
  items: IncidentSummary[];
  page: number;
  per_page: number;
  total: number;
}

export interface PlaybookRunSummary {
  run_id: string;
  incident_id: string;
  playbook_id: string;
  mode: RunMode;
  status: RunStatus;
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  notification_status: string | null;
}

export interface PlaybookRunDetail extends PlaybookRunSummary {
  steps: Array<Record<string, unknown>>;
  rollback: Record<string, unknown> | null;
  approval: Record<string, unknown> | null;
  notes: string | null;
}

export interface IncidentDetail extends IncidentSummary {
  correlation_strategy: string;
  affected_host_groups: string[];
  earliest_event_at: string;
  latest_event_at: string;
  raw_severities: string[];
  playbook_runs: PlaybookRunDetail[];
  // Fase 17 (DF17-07): link a Grafana Host Detail. null si AROC_GRAFANA_URL
  // no está configurado o el incidente no tiene host asociable.
  grafana_url: string | null;
}

export interface PlaybookRunList {
  items: PlaybookRunSummary[];
  page: number;
  per_page: number;
  total: number;
}

export interface SrpCatalogItem {
  protocol_id: string;
  name: string;
  description: string;
  classification: "safe-idempotent" | "manual-approval" | "destructive";
  client: string;
  version: number;
  target_executor: string;
  triggers: string[];
  // Equipos que tocan los pasos del SRP. Vacio = solo pasos locales de AROC.
  target_hosts: string[];
  steps_count: number;
  schedule_blocked: string | null;
}

export interface SrpCatalogList {
  items: SrpCatalogItem[];
  total: number;
}

// Catálogo derivado en DB (GET /api/v1/srps, I1). A diferencia de
// SrpCatalogItem expone `auto_execute` (estado auto vs manual del portal).
export interface SrpEntry {
  protocol_id: string;
  name: string;
  description: string;
  classification: "safe-idempotent" | "manual-approval" | "destructive";
  client: string;
  version: number;
  auto_execute: boolean;
  // tested = tiene ≥1 dry-run exitoso (requisito para encender auto_execute).
  tested: boolean;
  auto_execute_updated_by: string | null;
  auto_execute_updated_at: string | null;
  target_executor: string;
  triggers: string[];
  // Equipos que tocan los pasos del SRP. Vacio = solo pasos locales de AROC.
  target_hosts: string[];
  steps_count: number;
  schedule_blocked: string | null;
  synced_at: string;
}

export interface SrpEntryList {
  items: SrpEntry[];
  total: number;
}

export interface ModuleHealth {
  name: string;
  up: boolean | null;
  last_scrape_at: string | null;
  metrics: Record<string, number>;
  error: string | null;
}

export interface SystemHealthResponse {
  modules: ModuleHealth[];
  dry_run: boolean;
  analysis_enabled: boolean;
  // Aprobar es un acto humano: no depende de que haya modulo de analisis.
  approvals_enabled: boolean;
}

// ---- Fase 7 ----

export type TriggerType =
  | "automatic"
  | "manual"
  | "proactive"
  | "chat"
  | "telegram"
  | "repetido";
export type AnalysisStatus =
  | "active"
  | "awaiting_decision"
  | "completed"
  | "cancelled";

export interface TokenUsage {
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
  /** Pensamiento del modelo: ya va dentro de output_tokens. */
  thought_tokens?: number;
  /** Leídos de caché: fuera de input_tokens, con su propio precio. */
  cache_read_tokens?: number;
  /** Con el precio vigente de cada llamada. */
  estimated_cost_usd?: number;
  /** Alguna llamada fue a un modelo sin precio: el costo se queda corto. */
  cost_missing?: boolean;
  /** Motor de la última llamada (interno: "gemini", "anthropic"). */
  provider?: string | null;
}

/** Gasto de un turno, colgado del último mensaje de AROC del turno. */
export interface MessageUsage {
  input_tokens: number;
  output_tokens: number;
  thought_tokens: number;
  cache_read_tokens: number;
  estimated_cost_usd: number;
  cost_missing?: boolean;
  provider?: string;
}

export interface AnalysisSessionSummary {
  id: string;
  incident_id: string | null;
  trigger_type: TriggerType;
  target_host: string | null;
  started_by: string | null;
  started_at: string;
  finished_at: string | null;
  status: AnalysisStatus;
  model_id: string;
  token_usage: TokenUsage;
  // Primera pregunta del usuario, recortada (lista de pláticas).
  title?: string | null;
}

export interface AnalysisMessage {
  role:
    | "user"
    | "assistant"
    | "system"
    | "tool_call"
    | "tool_result"
    | "command";
  content: string;
  timestamp: string;
  tool_name?: string;
  tool_input?: Record<string, unknown>;
  tool_use_id?: string;
  command_name?: string;
  command_args?: string[];
  usage?: MessageUsage;
  /** AROC preguntó antes de consultar: el usuario elige una opción o escribe otra. */
  aclaracion?: Aclaracion;
}

export interface Aclaracion {
  termino: string;
  pregunta: string;
  opciones: string[];
}

export interface AnalysisSessionDetail extends AnalysisSessionSummary {
  messages: AnalysisMessage[];
}

export interface AnalysisSessionList {
  items: AnalysisSessionSummary[];
  page: number;
  per_page: number;
  total: number;
}

export type ApprovalStatus = "pending" | "approved" | "rejected" | "expired";

export interface PendingApprovalAction {
  playbook_id: string;
  incident_id: string | null;
  reason: string;
  params: Record<string, unknown>;
  mode?: string;
  // Diff legible (antes → después) de un cambio de configuración, p.ej.
  // AROC-P-059/060 (NAT de la Raspberry AT&T, monitores de ZM1).
  diff?: string;
  // Aprobaciones del buzón de AROC (action_type correo_respuesta / correo_escalamiento).
  de?: string;
  asunto?: string;
  caso?: string;
  veredicto?: string;
  resumen?: string;
  evidencia?: string[];
  borrador?: string;
  para?: string[];
  cc?: string[];
  responsable?: string;
  alerta?: string | null;
}

export interface PendingApproval {
  id: string;
  analysis_session_id: string;
  action_type: string;
  action_payload: PendingApprovalAction;
  status: ApprovalStatus;
  requested_at: string;
  decided_at: string | null;
  decided_by: string | null;
  decided_via: string | null;
  rejection_reason: string | null;
  expiration_minutes: number;
}

export interface BudgetResponse {
  enabled: boolean;
  used: number;
  limit: number;
  percentage_used: number;
  resets_at: string | null;
}

// ---- Fase 18: diagnóstico profundo ----

export type DiagnosticStatus =
  | "pending"
  | "approved"
  | "rejected"
  | "executed"
  | "failed"
  | "timeout";

export interface DiagnosticRun {
  id: string;
  session_id: string;
  host: string;
  command_name: string;
  rendered_command: string;
  params: Record<string, string>;
  reason: string;
  status: DiagnosticStatus;
  approved_by: string | null;
  approved_at: string | null;
  output: string | null;
  output_truncated: boolean;
  exit_code: number | null;
  duration_ms: number | null;
  executed_at: string | null;
  error: string | null;
  created_at: string;
}

export interface DiagnosticRunList {
  items: DiagnosticRun[];
  used: number;
  limit: number;
}

// ---- Fase 10: licencia ----

export type LicenseStatusValue =
  | "valid"
  | "grace_period"
  | "expired"
  | "revoked"
  | "restricted"
  | "unknown";

export interface LicenseStatusResponse {
  status: LicenseStatusValue;
  restricted: boolean;
  warning: boolean;
  license_id: string | null;
  client_name: string | null;
  tier: string | null;
  expires_at: string | null;
  grace_until: string | null;
  last_validated_at: string | null;
  last_validation_error: string | null;
}

// ---- Fase 12: reportes ciclicos ----

export type ReportType = "monthly" | "weekly" | "biweekly";

export interface GeneratedReportSummary {
  id: string;
  report_type: ReportType;
  period_start: string;
  period_end: string;
  generated_at: string;
  generated_by: string;
  pdf_available: boolean;
  html_available: boolean;
  email_sent: boolean;
  email_sent_at: string | null;
  ai_tokens_used: number;
}

export interface GeneratedReportDetail extends GeneratedReportSummary {
  email_recipients: string[] | null;
  ai_recommendations: string | null;
  metadata: Record<string, unknown>;
}

export interface GeneratedReportListPage {
  items: GeneratedReportSummary[];
  page: number;
  per_page: number;
  total: number;
}

export interface GenerateReportRequest {
  report_type?: ReportType;
  period_start?: string;
  period_end?: string;
  force?: boolean;
}

export interface GenerateReportResponse {
  report_id: string;
  status: "pending";
  report_type: ReportType;
  period_start: string;
  period_end: string;
}

// ---- F14: sugerencias de hosts para el form de Nuevo análisis ----

export type SuggestedHostCategory =
  | "network"
  | "telephony"
  | "video"
  | "server"
  | "other";

export interface SuggestedHost {
  hostname: string;
  name: string;
  category: SuggestedHostCategory;
  is_critical: boolean;
}

export interface SuggestedHostsResponse {
  hosts: SuggestedHost[];
  source: "live" | "cache" | "unavailable";
}

// ---- F14: dashboards de Grafana embebidos ----

export interface DashboardEmbed {
  id: string;
  title: string;
  category: string;
  url: string;
}

export interface DashboardEmbedList {
  items: DashboardEmbed[];
}

// ---- Fase 19: ECC — Evaluación de Calidad Corporativa ----

export type EccArea = "nova" | "crea" | "soporte" | "custodias";

export interface EccObservationPreset {
  code: string;
  label: string;
  checked: boolean;
}

export interface EccObservationCustom {
  label: string;
}

export interface EccObservations {
  presets: EccObservationPreset[];
  custom: EccObservationCustom[];
}

export type EccReportStatus =
  | "draft"
  | "proposed"
  | "official"
  | "rejected"
  | "archived";

export type EccRunStatus =
  | "pending"
  | "fetching_cdr"
  | "downloading"
  | "transcribing"
  | "analyzing"
  | "generating"
  | "done"
  | "failed";

export type EccRecordingSource = "asterisk" | "media1";

// CDR entry from Asterisk asteriskcdrdb.cdr
export interface EccCdrEntry {
  uniqueid: string;
  calldate: string;
  src: string;
  dst: string;
  duration: number;
  billsec: number;
  disposition: "ANSWERED" | "NO ANSWER" | "BUSY" | "FAILED";
  recordingfile: string;
  source: EccRecordingSource;
}

export interface EccCdrPreview {
  period_start: string;
  period_end: string;
  area: EccArea;
  total_calls: number;
  answered_calls: number;
  recordings_available: number;
  entries: EccCdrEntry[];
  // Cuántas grabaciones están en Asterisk (hoy) vs media1 (días anteriores)
  source_breakdown: { asterisk: number; media1: number };
}

export interface EccSrpSummary {
  id: string;
  area: EccArea;
  name: string;
  description: string;
  status: EccReportStatus;
  output_format: "docx" | "xlsx";
  created_at: string;
  updated_at: string;
  proposed_by: string | null;
  approved_by: string | null;
  default_observations: Array<{ code: string; label: string; description: string }>;
  // SRP de solo-transcripción: el pipeline emite la transcripción completa sin
  // análisis ni LLM. El wizard oculta "Puntos a analizar" cuando es true.
  transcription_only: boolean;
  // SRP que depende de la agenda de escalación del cliente: el wizard pregunta
  // si hubo cambios antes de dejar lanzar la corrida.
  requiere_agenda: boolean;
  agenda_cliente: string | null;
}

export interface EccAgenda {
  cliente: string;
  total: number;
  agencias: number;
  actualizada_en: string | null;
}

export interface EccAgendaSubida {
  cliente: string;
  contactos: number;
  agencias: number;
  actualizada_en: string;
  /** Filas del archivo que no se cargaron, con el motivo. */
  omitidas: string[];
}

export interface EccSrpList {
  items: EccSrpSummary[];
}

export interface EccRunSummary {
  id: string;
  area: EccArea;
  srp_id: string;
  srp_name: string;
  status: EccRunStatus;
  period_start: string;
  period_end: string;
  started_by: string;
  started_at: string;
  finished_at: string | null;
  output_file: string | null;
  error: string | null;
  recordings_total: number;
  recordings_transcribed: number;
  // DFECC-10
  observations: EccObservations | null;
  // DFECC-11
  parent_run_id: string | null;
  followup_kind: "drilldown" | "secondary" | null;
  followup_focus: Record<string, unknown> | null;
  children?: EccRunSummary[];
}

export interface EccRunList {
  items: EccRunSummary[];
  page: number;
  per_page: number;
  total: number;
}

export interface EccRunDetail extends EccRunSummary {
  log_lines: string[];
  cdr_count: number;
}

export interface EccHubStats {
  area: EccArea;
  runs_last_7d: number;
  last_run_at: string | null;
  last_run_status: EccRunStatus | null;
  pending_approvals: number;
  official_srps: number;
}

export interface EccStartRunRequest {
  area: EccArea;
  srp_id: string;
  period_start: string;
  period_end: string;
  observations?: EccObservations;
  // Requerido por los SRPs con `requiere_agenda`: el backend rechaza la
  // corrida si no viene, y la bloquea si viene en false.
  agenda_sin_cambios?: boolean;
}

export interface EccStartRunResponse {
  run_id: string;
  status: EccRunStatus;
}

export interface EccFollowupRequest {
  kind: "drilldown" | "secondary";
  focus: Record<string, unknown>;
  observations?: EccObservations;
}

export interface EccFollowupResponse {
  run_id: string;
  status: EccRunStatus;
}

// ── Descarga ad-hoc de llamadas por teléfono/extensión ──────────────────────

export type EccCallJobStatus =
  | "queued"
  | "searching"
  | "results_ready"
  | "downloading"
  | "done"
  | "failed";

export interface EccCallResult {
  uniqueid: string;
  calldate: string;
  src: string;
  dst: string;
  duration: number;
  disposition: string;
  recordingfile: string;
}

export interface EccCallJob {
  id: string;
  number: string;
  status: EccCallJobStatus;
  period_start: string;
  period_end: string;
  started_by: string;
  started_at: string;
  finished_at: string | null;
  results: EccCallResult[];
  selected: string[];
  has_output: boolean;
  error: string | null;
  log_lines: string[];
}

export interface EccCallSearchRequest {
  number: string;
  period_start: string;
  period_end: string;
}

export interface EccCallDownloadRequest {
  uniqueids: string[];
  // El operador decide si además del audio quiere la transcripción. Opcional:
  // el backend asume true si no viene (comportamiento previo al cambio).
  transcribe?: boolean;
}

export interface EccCallJobRef {
  job_id: string;
  status: EccCallJobStatus;
}

export interface EccCallJobList {
  items: EccCallJob[];
  page: number;
  per_page: number;
  total: number;
}

// ---- ADR_006: Portal de creación de SRPs ----

export interface SrpObservation {
  code: string; // regex [A-Z]\d+, max 8
  label: string; // 1-200
  description?: string; // <=500
}

export interface SrpConfig {
  source?: string;
  default_observations: SrpObservation[];
  workaround?: boolean;
  // extra="allow" en backend → cualquier campo custom adicional es válido
  [key: string]: unknown;
}

export interface CreateSrpRequest {
  area: EccArea;
  name: string;
  description: string;
  output_format: "docx" | "xlsx";
  config: SrpConfig;
}

export interface CreateSrpResponse {
  id: string;
  status: EccReportStatus;
}

export interface UpdateSrpRequest {
  name?: string;
  description?: string;
  output_format?: "docx" | "xlsx";
  config?: SrpConfig;
}

export interface CloneSrpRequest {
  new_name?: string;
}

export interface CloneSrpResponse {
  id: string;
  name: string;
  status: EccReportStatus;
}

export interface DryRunSrpRequest {
  period_date: string; // YYYY-MM-DD
  srp_id?: string;
  candidate?: CreateSrpRequest;
}

export interface DryRunCriteriaStat {
  code: string;
  label: string;
  matched: number;
  pct: number;
}

export interface DryRunSrpResponse {
  calls_total: number;
  calls_with_recording: number;
  by_criterion: DryRunCriteriaStat[];
  sample_uniqueids: string[];
  warnings: string[];
}

export interface SrpDetail {
  id: string;
  area: EccArea;
  name: string;
  description: string;
  status: EccReportStatus;
  output_format: "docx" | "xlsx";
  config: SrpConfig;
  proposed_by: string | null;
  proposed_at: string | null;
  approved_by: string | null;
  approved_at: string | null;
  rejection_comment: string | null;
  created_at: string;
  updated_at: string;
}

// ---- ADR_007: Admin UI de Telegram ----

export interface TelegramDestinationTrafico {
  event_type: string;
  total: number;
}

export interface TelegramDestination {
  id: string;
  name: string; // slug [a-z0-9_]+
  label: string;
  chat_id: string;
  enabled: boolean;
  shadow_mode: boolean;
  description: string;
  created_at: string;
  updated_at: string;
  /** Patrones de las reglas de ruteo activas que apuntan a este destino. */
  patrones_ruteo?: string[];
  /** Lo que este destino recibio DE VERDAD en los ultimos 7 dias. */
  trafico_7d?: TelegramDestinationTrafico[];
}

export interface TelegramDestinationList {
  items: TelegramDestination[];
}

export interface CreateTelegramDestinationRequest {
  name: string;
  label: string;
  chat_id: string;
  enabled?: boolean;
  shadow_mode?: boolean;
  description?: string;
}

export interface UpdateTelegramDestinationRequest {
  label?: string;
  chat_id?: string;
  enabled?: boolean;
  shadow_mode?: boolean;
  description?: string;
}

export interface TelegramTestRequest {
  destination_name: string;
  message?: string;
}

export interface TelegramTestResponse {
  destination: string;
  status: "sent" | "shadow" | "disabled";
}

export interface TelegramBotHealth {
  connected: boolean;
  username: string | null;
  bot_id: number | null;
  last_event_at: string | null;
  real_send_tracked: boolean;
  error: string | null;
}

export interface TelegramStats {
  active_count: number;
  shadow_count: number;
  disabled_count: number;
  events_today: number;
}

// ---- ADR_008: Tab Historial ensayo (PR-B Telegram) ----

// El desenlace que tuvo el evento en el dispatcher. Desde la migracion 062 se
// guardan los cuatro; antes solo se guardaba `shadow`, asi que las filas
// anteriores a esa fecha son todas de ensayo.
export type TelegramResultado = "sent" | "shadow" | "disabled" | "send_failed";

export interface TelegramShadowLogEntry {
  id: string;
  destination_name: string;
  event_type: string;
  rendered_text: string;
  resultado: TelegramResultado;
  decision_reason: string;
  payload: Record<string, unknown>;
  would_send_at: string;
}

export interface TelegramShadowLogList {
  items: TelegramShadowLogEntry[];
  total: number;
  page: number;
  per_page: number;
}

export interface TelegramShadowLogStats {
  last_24h: number;
  last_7d: number;
  distinct_destinations_today: number;
  distinct_event_types_24h: number;
}

export interface TelegramShadowLogFilters {
  destination_name?: string;
  event_type?: string;
  resultado?: TelegramResultado;
  since?: string; // ISO 8601 con `Z`
  until?: string;
  page?: number;
  per_page?: number;
}

// ---- ADR_011: Gestion y Auditoria de Configuracion ----

export interface ConfigSite {
  id: string;
  client: string;
  name: string;
  code: string | null;
  subnet: string | null;
  created_at: string;
}

export interface ConfigSiteList {
  items: ConfigSite[];
}

export interface ConfigDevice {
  id: string;
  site_id: string;
  site_name: string | null;
  device_type: string;
  role: string | null;
  ip: string;
  hostname: string | null;
  model: string | null;
  // La escribe la cosecha, leida del propio equipo. null = ese equipo aun no
  // ha cosechado desde que existe la columna.
  firmware_version: string | null;
  serial_number: string | null;
  status: string;
  last_seen_at: string | null;
  created_at: string;
}

export interface ConfigDeviceList {
  items: ConfigDevice[];
  total: number;
  page: number;
  per_page: number;
}

export interface ConfigDeviceFilters {
  site_id?: string;
  device_type?: string;
  client?: string;
  status?: string;
  page?: number;
  per_page?: number;
}

export interface ConfigDeviceConfigSummary {
  id: string;
  captured_at: string;
  captured_by: string;
  payload_hash: string;
  is_known_good: boolean;
  correlated_zabbix_events_count: number;
}

export interface ConfigDeviceConfigList {
  items: ConfigDeviceConfigSummary[];
  total: number;
  page: number;
  per_page: number;
}

export interface ConfigDeviceConfig {
  id: string;
  device_id: string;
  captured_at: string;
  captured_by: string;
  payload: Record<string, unknown>;
  payload_hash: string;
  is_known_good: boolean;
  marked_good_by: string | null;
  marked_good_at: string | null;
  correlated_zabbix_events: number[];
}

export type DiffChangeType = "added" | "removed" | "changed" | "type_changed";

export interface DiffChange {
  path: (string | number)[];
  type: DiffChangeType;
  old: unknown;
  new: unknown;
}

export interface DiffSummary {
  added: number;
  removed: number;
  changed: number;
  type_changed: number;
}

export interface ConfigDiffResponse {
  from_config_id: string | null;
  from_captured_at?: string;
  to_config_id: string;
  to_captured_at?: string;
  changes: DiffChange[];
  summary: DiffSummary;
  warning?: string;
}

// ---- ADR_012: Gestión de Configuración por Perfiles ----

export interface ConfigurationProfile {
  id: string;
  name: string;
  description: string;
  device_type: string;
  status: string;
  collection_method: string;
  credentials_ref: string;
  fields_to_audit: string[];
  schedule_cron: string;
  zabbix_host_group: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ConfigurationProfileSummary {
  id: string;
  name: string;
  device_type: string;
  status: string;
  collection_method: string;
  schedule_cron: string;
  devices_count: number;
  devices_with_unreviewed_changes: number;
  last_capture_at: string | null;
}

export interface ConfigurationProfileList {
  items: ConfigurationProfileSummary[];
  total: number;
  page: number;
  per_page: number;
}

export interface CreateConfigurationProfileRequest {
  name: string;
  description: string;
  device_type: string;
  collection_method: string;
  credentials_ref: string;
  fields_to_audit: string[];
  schedule_cron: string;
  zabbix_host_group?: string | null;
}

export interface FieldCatalogItem {
  key: string;
  label: string;
  category: string;
}

export interface FieldCatalogResponse {
  device_type: string;
  items: FieldCatalogItem[];
}

export interface ZabbixHostGroup {
  groupid: string;
  name: string;
}

export interface ZabbixHostGroupList {
  items: ZabbixHostGroup[];
}

export interface ZabbixHostInterface {
  ip: string | null;
  dns: string | null;
  useip: string | null;
  main: string | null;
}

export interface ZabbixHost {
  hostid: string;
  host: string;
  name: string;
  status: string;
  interfaces: ZabbixHostInterface[];
}

export interface ZabbixHostList {
  items: ZabbixHost[];
}

// ─────────────────────────────────────────────────────────────────────────
// ADR_012 PR-CMgmt-G — Rollback con TOTP.
// ─────────────────────────────────────────────────────────────────────────

export type RollbackStatus =
  | "pending_apply"
  | "applied"
  | "failed"
  | "cancelled";

export type RollbackKind = "device" | "profile";

export interface RollbackDeviceRequest {
  totp_code: string; // 6 digitos
  target_config_id?: string | null;
  notes?: string | null;
}

export interface RollbackProfileRequest {
  totp_code: string; // 6 digitos
  target?: "known_good";
  notes?: string | null;
}

export interface RollbackAuditEntry {
  id: string;
  requested_by: string;
  device_id: string;
  profile_id: string | null;
  from_config_id: string;
  to_config_id: string | null;
  status: RollbackStatus;
  kind: RollbackKind;
  requested_at: string;
  applied_at: string | null;
  notes: string | null;
  retry_count: number;
  last_error: string | null;
}

export interface RollbackDeviceResponse {
  audit_entry: RollbackAuditEntry;
  message: string;
}

export interface RollbackProfileResponse {
  profile_id: string;
  devices_queued: number;
  audit_entries: RollbackAuditEntry[];
  message: string;
}

export interface RollbackAuditList {
  items: RollbackAuditEntry[];
  total: number;
  page: number;
  per_page: number;
}

// ─────────────────────────────────────────────────────────────────────────
// PR-CMgmt-H — Catálogo de device_types con filtros en cascada.
// ─────────────────────────────────────────────────────────────────────────

export interface CollectionMethodOption {
  value: string;
  label: string;
}

export interface CredentialsOption {
  value: string;
  label: string;
}

export interface DeviceTypeCatalogEntry {
  value: string;
  label: string;
  description: string;
  implemented: boolean;
  methods: CollectionMethodOption[];
  credentials_by_method: Record<string, CredentialsOption[]>;
}

export interface DeviceTypeCatalogResponse {
  items: DeviceTypeCatalogEntry[];
}

// ─────────────────────────────────────────────────────────────────────────
// Control de Configuración — drift accionable (cambios detectados → revertir).
// Vista agregada por campo: el último snapshot de cada equipo vs lo esperado
// (timezone esperado por región vía mapa canónico; demás campos vs known_good).
// ─────────────────────────────────────────────────────────────────────────

export type DriftSeverity = "high" | "medium" | "low";

export interface ConfigDriftRow {
  device_id: string;
  site: string; // agencia (nombre)
  site_code: string | null;
  position: string | null; // role/posición (principal_entrada…)
  ip: string;
  model: string | null;
  field: string; // clave dotted, p.ej. "time.timezone"
  field_label: string; // legible, p.ej. "Zona horaria"
  actual: string; // valor actual legible
  expected: string; // valor esperado legible
  severity: DriftSeverity;
  reverted_count: number; // ×N veces que ya se revirtió (señal de raíz)
  detected_at: string | null; // ISO (null si nunca se cosechó)
  // true = el equipo no respondió (apagado/caído/nunca cosechado). No es deriva
  // de config: no se puede afirmar "conforme". Se muestra como fila propia.
  sin_datos?: boolean;
}

export interface ConfigDriftFieldStat {
  field: string;
  field_label: string;
  count: number;
}

export interface ConfigDriftSummary {
  devices: number; // equipos auditados (online)
  with_drift: number; // equipos con al menos un cambio
  by_field: ConfigDriftFieldStat[];
}

export interface ConfigDriftResponse {
  generated_at: string;
  scope: string; // p.ej. "holanda"
  last_audit_at: string | null;
  summary: ConfigDriftSummary;
  rows: ConfigDriftRow[];
}

export interface ConfigDriftFilters {
  scope?: string;
  field?: string;
  site?: string;
  severity?: DriftSeverity;
  device_type?: string;
}

// Revertir un campo en uno o varios equipos a la vez (acción masiva por campo).
export interface RevertFieldRequest {
  totp_code: string; // 6 dígitos
  field: string;
  device_ids: string[];
  notes?: string | null;
}

export interface RevertFieldResponse {
  field: string;
  devices_queued: number;
  message: string;
}

// ── Portal Admin Usuarios ──────────────────────────────────────────────────

export interface AdminUserAreaRole {
  area_id: string;
  area_code: string;
  area_name: string;
  role: "operator" | "manager";
}

export interface AdminUserSummary {
  id: string;
  username: string;
  role: string;
  is_active: boolean;
  has_2fa: boolean;
  telegram_chat_id: string | null;
  global_roles: string[];
  segment_count: number;
  area_count: number;
  created_at: string;
  last_login_at: string | null;
}

export interface AdminUserDetail {
  id: string;
  username: string;
  role: string;
  is_active: boolean;
  has_2fa: boolean;
  telegram_chat_id: string | null;
  global_roles: string[];
  segments: string[];
  area_roles: AdminUserAreaRole[];
  created_at: string;
  last_login_at: string | null;
}

export interface AdminUserList {
  items: AdminUserSummary[];
  total: number;
}

export interface AdminUserCreateResponse {
  user: AdminUserDetail;
  temp_password: string;
}

export interface AdminSegmentRead {
  id: string;
  code: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface AdminSegmentList {
  items: AdminSegmentRead[];
  total: number;
}

export interface AdminAreaRead {
  id: string;
  segment_id: string;
  code: string;
  name: string;
  description: string | null;
  is_active: boolean;
}

export interface AdminAreaList {
  items: AdminAreaRead[];
  total: number;
}

export interface AdminAuditLogRead {
  id: string;
  actor_id: string;
  action: string;
  target_type: string;
  target_id: string | null;
  before_value: unknown;
  after_value: unknown;
  ip_address: string | null;
  timestamp: string;
}

export interface AdminAuditLogList {
  items: AdminAuditLogRead[];
  total: number;
  next_cursor: string | null;
}

// Carritos Holanda — pantalla de atención (ADR_004 Fase 1)
export type HolandaState =
  | "OFFLINE"
  | "ACTIVO_1"
  | "ACTIVO_2"
  | "VIGILANDO"
  | "ALARMA"
  | "NOTIF_GUARDIA"
  | "POSIBLE_RUTA"
  | "WARNING"
  // El medidor del tablero no transmite: no es "no hay carrito", es "no se ve".
  | "SIN_DATO";

export type HolandaReading = "dos_carritos" | "un_carrito" | "consumo_minimo" | "cero";

export interface HolandaChannel {
  channel_num: number;
  label: string;
  state: HolandaState;
  watts: number;
  baseline_watts: number | null;
  cart_count: number;
  minutes_in_state: number;
  reading: HolandaReading;
  cleared: boolean; // episodio ya limpiado por el monitorista (fuera de cola/KPIs)
  carritos_grafica?: number | null; // 1 ó 2 según la gráfica; null = aún no se sabe
  padron?: number; // posiciones registradas (IMEI; el mismo padrón que Axelia)
  coincide_padron?: boolean | null; // gráfica vs padrón; null = sin con qué comparar
  parcial?: boolean; // 1 de 2 desconectado, el otro sigue enfriando
}

export interface HolandaSite {
  device_gid: string;
  site_name: string;
  agency_code: string;
  agency_name: string;
  carts: number;
  alarms: number;
  watching: number;
  channels: HolandaChannel[];
}

export interface HolandaTicketBrief {
  id: string;
  status: string; // abierto | en_atencion | resuelto
  attended_by: string | null;
  notes_count: number;
}

export interface HolandaTicketNote {
  at: string;
  by: string;
  text: string;
}

export interface HolandaTicket {
  id: string;
  device_gid: string;
  channel_num: number;
  site_name: string;
  opened_state: string;
  status: string;
  attended_by: string | null;
  attended_at: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
  disposition: string | null; // motivo de la limpieza (sólo al resolver)
  notes: HolandaTicketNote[];
  created_at: string;
  updated_at: string;
}

// Motivos de limpieza (deben coincidir con HolandaDisposition del backend).
export const HOLANDA_DISPOSITIONS: { value: string; label: string }[] = [
  { value: "reportado", label: "Ya lo reporté" },
  { value: "atendido", label: "Ya lo atendí" },
  { value: "falso_positivo", label: "Falso positivo" },
  { value: "deshielo", label: "Deshielo" },
  { value: "ya_no_existe", label: "Ya no existe / no está" },
  { value: "otro", label: "Otro" },
];

export interface HolandaAlert {
  device_gid: string;
  site_name: string;
  channel_num: number;
  label: string;
  state: HolandaState;
  watts: number;
  cart_count: number;
  minutes_in_state: number;
  ticket: HolandaTicketBrief | null;
  parcial?: boolean; // 1 de 2 desconectado, el otro sigue enfriando
}

export interface HolandaSummary {
  sites: number;
  channels: number;
  carts: number;
  active: number;
  watching: number;
  notifying: number;
  alarms: number;
  possible_route: number;
  warning: number;
  offline: number;
  sin_dato?: number;
  tableros_sin_senal?: number;
  tomas_dos?: number; // tomas que la gráfica sabe de dos carritos
  parciales?: number; // tomas de dos con uno apagado y el otro enfriando
  updated_minutes_ago: number | null;
}

export interface HolandaOverview {
  summary: HolandaSummary;
  alerts: HolandaAlert[];
  sites: HolandaSite[];
}

// ── Reglas de ruteo de alarmas ──────────────────────────────────────────────
//
// Que alarma va a que grupo. Antes vivia en el codigo, buscando la etiqueta
// `module` que ningun disparador de esta casa trae: por eso los carritos caian
// en el grupo equivocado. Lo que si existe son los GRUPOS DE HOST.

export interface ReglaRuteo {
  id: number;
  posicion: number;
  tipo: "host_group" | "tag";
  /** Nombre de la etiqueta cuando `tipo === "tag"`. Vacio en `host_group`. */
  campo: string;
  patron: string;
  destino: string;
  activa: boolean;
  descripcion: string;
}

export interface ReglaRuteoList {
  items: ReglaRuteo[];
  /** Destinos que existen hoy: una regla que apunte a otro pierde la alarma. */
  destinos_validos: string[];
}

export type CrearReglaRuteoRequest = Omit<ReglaRuteo, "id">;
export type ActualizarReglaRuteoRequest = Partial<Omit<ReglaRuteo, "id">>;

/** Una alarma vigente y a donde caeria con las reglas de ahora. */
export interface SimulacionAlarma {
  host: string;
  grupo: string;
  trigger: string;
  /** `null` = no se manda (severidad baja y ninguna regla la reclama). */
  destino: string | null;
  /** Que regla la reclamo. `null` = cayo en la de por omision. */
  regla_id: number | null;
}

export interface SimulacionRuteo {
  items: SimulacionAlarma[];
  total: number;
}

// ── Quien puede usar el menu del bot ────────────────────────────────────────
//
// Antes vivia en una variable de entorno: agregar a alguien exigia SSH y
// recrear el contenedor. El poller lee la tabla POR MENSAJE, asi que un alta
// surte efecto de inmediato.

export interface UsuarioMenu {
  id: number;
  /** El id NUMERICO de Telegram, no el @usuario: el arroba cambia, el id no. */
  telegram_id: number;
  nombre: string;
  activo: boolean;
  descripcion: string;
}

export interface UsuarioMenuList {
  items: UsuarioMenu[];
}

export type CrearUsuarioMenuRequest = Omit<UsuarioMenu, "id">;
export type ActualizarUsuarioMenuRequest = Partial<Omit<UsuarioMenu, "id" | "telegram_id">>;

// Comandos para dejar un equipo en golden (GET /config/devices/{id}/planchado).
// Solo muestra los comandos; el admin los corre. NO ejecuta nada.
// En un RUT son `uci` por SSH; en un biometrico, peticiones ISAPI por curl.
export interface ConfigPlanchado {
  device_id: string;
  ip: string;
  campos_drift: string[];
  en_golden: boolean;
  comandos: string;
  // Solo biometricos:
  site?: string;
  detalle?: Record<string, { actual: string; esperado: string }>;
  // false = el ultimo snapshot no se pudo leer. NO es lo mismo que en_golden:
  // sin este dato, un equipo apagado se pintaria como conforme.
  auditado?: boolean;
}

// Respuesta de POST /config/recosechar: cuantos equipos se encolaron para
// recosecha en segundo plano. La tabla de deriva se refresca sola al terminar.
export interface ConfigRecosechaResponse {
  encolados: number;
  device_type: string;
  // Perfiles activos sobre este tipo de equipo. Con MAS DE UNO la eleccion de
  // perfil es indeterminista y la barrida puede correr uno distinto del que el
  // operador cree; la pantalla lo avisa.
  perfiles_activos: string[];
}

// Conteos para las graficas de la pantalla de incidentes
// (GET /api/v1/incidents/estadisticas).
export interface ConteoPorClave {
  clave: string;
  total: number;
}

export interface IncidentesEstadisticas {
  general: ConteoPorClave[];
  top_intervencion: ConteoPorClave[];
  top_abiertos: ConteoPorClave[];
  top_resueltos: ConteoPorClave[];
  ventana_resueltos_dias: number;
  total_abiertos: number;
  total_intervencion: number;
}

// Que equipos de un incidente siguen caidos ahora
// (GET /api/v1/incidents/{id}/equipos). `zabbix_disponible=false` significa
// "no pude preguntar", que NO es lo mismo que "todo recuperado".
export interface IncidentEquipos {
  activos: string[];
  recuperados: string[];
  zabbix_disponible: boolean;
}

// Respuesta de POST /config/devices/{id}/planchar/aplicar (ejecuta el uci en el
// router). aplicado=false + motivo cuando no hubo nada que hacer o falló.
export interface ConfigPlancharAplicado {
  aplicado: boolean;
  campos_drift: string[];
  salida?: string;
  motivo?: string;
}

// Plan y resultado de POST /config/planchar-flota.
//
// NACE EN SECO: sin `aplicar=true` sólo dice qué haría, sin abrir una sola
// conexión. Escribir en cientos de equipos del cliente no puede ser lo que
// pasa por omisión al pulsar un botón.
export interface PlanchadoFlotaEquipo {
  device_id: string;
  ip: string;
  familia: string;
  agencia: string | null;
  campos?: string[];
  capturado?: string | null;
  // Horas desde la última lectura. Se plancha contra esa foto: si es de hace
  // días se está corrigiendo un estado que quizá ya cambió.
  antiguedad_h?: number | null;
  incluye_zona_horaria?: boolean;
  motivo?: string;
}

export interface PlanchadoFlotaResumen {
  a_planchar: number;
  ya_en_golden: number;
  // NO SE SABE cómo están: su última foto no se pudo leer. Es distinto de
  // "conforme", y juntarlos hace que el botón diga "todo listo" habiendo
  // ignorado a media flota.
  no_leidos: number;
  con_zona_horaria: number;
  foto_mas_vieja_h: number | null;
  por_familia: Record<string, number>;
}

export interface PlanchadoFlotaRespuesta {
  modo: "en_seco" | "aplicado";
  total: number;
  a_planchar: PlanchadoFlotaEquipo[];
  ya_en_golden: PlanchadoFlotaEquipo[];
  no_leidos: PlanchadoFlotaEquipo[];
  resumen: PlanchadoFlotaResumen;
  // Familias cuya deriva se sabe calcular pero que nadie sabe escribir aún.
  sin_aplicador: Record<string, number>;
  // Sólo en modo aplicado:
  planchados?: number;
  fallaron?: number;
  // La zona horaria se cuenta APARTE porque HikCentral la repone: 33
  // reversiones medidas, entre 6 segundos y 16 horas después del planchado.
  // `zona_horaria_revirtio_ya` son los que se deshicieron dentro de la ventana
  // de verificación; los que revierten horas después NO se ven aquí.
  zona_horaria_quedo?: number;
  zona_horaria_revirtio_ya?: number;
  bloqueados_por_candado?: number;
  familias_permitidas?: string[];
}

// ── HikCentral: inventario y estado de la plataforma (VMS1) ────────────────
// No es deriva de configuracion: el OpenAPI de HikCentral no expone la config
// de los equipos. Lo que da, y no teniamos, es inventario + estado de los 400+
// equipos en una llamada, y la fragmentacion de firmware por modelo.

export interface HikCentralAgencia {
  agencia: string;
  total: number;
  en_linea: number;
  caidos: number;
  // "caida" = lleva el umbral completo abajo (incidente). "transitorio" = esta
  // abajo pero no se puede demostrar la duracion (p.ej. reinicio de router).
  veredicto: "caida" | "transitorio" | "ok";
}

export interface HikCentralFirmwareModelo {
  golden: string;
  en_golden: number;
  fuera: number;
  versiones: Record<string, number>;
}

export interface HikCentralResponse {
  _status: "ok" | "sin_datos" | "sin_configurar";
  mensaje?: string;
  motivo?: string | null;
  ultima_lectura?: string | null;
  plataforma?: { produceName?: string; softVersion?: string };
  capturado_en?: string;
  lecturas_disponibles?: number;
  minutos_para_caida?: number;
  totales?: {
    equipos: number;
    en_alcance: number;
    fuera_de_alcance: number;
    en_linea: number;
    caidos: number;
  };
  agencias?: HikCentralAgencia[];
  resumen_agencias?: { caida: number; transitorio: number; ok: number };
  firmware?: Record<string, HikCentralFirmwareModelo>;
  firmware_fuera_de_golden?: number;
  generated_at?: string;
}

// ── Camaras de HikCentral ──────────────────────────────────────────────────
// Lo que la pantalla tiene que decir antes que el total: una camara caida cuyo
// grabador tambien lo esta NO es una falla de la camara. El 7-sep-2026, de 167
// caidas, 157 colgaban de 86 grabadores y solo 10 eran fallas propias.

export interface CamaraFila {
  index_code: string;
  nombre: string | null;
  area: string | null;
  agencia: string | null;
  fuera_de_alcance: boolean;
  grabador_index: string;
  grabador: string | null;
  grabador_ip: string;
  // Modelo de fábrica del equipo (sale de su código), o null si no lo trae.
  modelo: string | null;
  // `termica` / `fija` / `ptz` = la cámara ES ese equipo y tiene IP propia, así
  // que se le puede preguntar su configuración. `canal_nvr` = cuelga de un
  // grabador y sólo se sabe de ella lo que diga la plataforma.
  rol: "termica" | "fija" | "ptz" | "canal_nvr" | "desconocido";
  ip_propia: boolean;
  ptz: boolean;
  // `false` = sin plan de grabacion en la plataforma. No prueba que el NVR no
  // grabe por su cuenta; si prueba que desde HikCentral no se puede sacar ese
  // video.
  graba: boolean;
  estado?: number;
  estado_grabador?: number;
  causa?: "ok" | "grabador_caido" | "camara_caida";
}

export interface CamarasArea {
  area: string;
  agencia: string | null;
  total: number;
  en_linea: number;
  caidas: number;
  caidas_por_grabador: number;
  caidas_propias: number;
  sin_grabacion: number;
  sin_grabacion_en_linea: number;
  termicas: number;
  termicas_en_linea: number;
  con_ip_propia: number;
}

export interface CamarasRol {
  total: number;
  en_linea: number;
  caidas: number;
  sin_grabacion: number;
  con_ip_propia: number;
}

export interface CamarasNovedadCambio {
  index_code: string;
  nombre: string | null;
  area: string | null;
  campo: string;
  antes: unknown;
  ahora: unknown;
}

export interface CamarasResponse {
  _status: "ok" | "sin_datos" | "sin_configurar";
  mensaje?: string;
  motivo?: string | null;
  ultima_lectura?: string | null;
  plataforma?: { produceName?: string; softVersion?: string };
  capturado_en?: string;
  comparado_con?: string | null;
  totales?: {
    camaras: number;
    en_alcance: number;
    fuera_de_alcance: number;
    sin_grabacion: number;
    sin_grabacion_en_linea: number;
    en_linea: number;
    caidas: number;
    caidas_por_grabador: number;
    caidas_propias: number;
  };
  grabadores?: {
    con_camaras: number;
    sin_plan_completo: number;
    con_plan_completo: number;
    mezclados: number;
  };
  por_rol?: Record<string, CamarasRol>;
  areas?: CamarasArea[];
  camaras?: CamaraFila[];
  novedades?: {
    altas: CamaraFila[];
    bajas: CamaraFila[];
    cambios: CamarasNovedadCambio[];
  };
  resumen_causas?: { por_grabador: number; propias: number };
  generated_at?: string;
}

// ── Cámaras: configuración, golden y cumplimiento ──────────────────────────
// Tres categorías, y la tercera es la que suele faltar: una cámara que no se
// pudo leer NO incumple. Hoy son 100 de 143, casi todas por falta de ruta.

export interface CamaraDesvio {
  campo: string;
  actual: string | null;
  esperado: string;
  sin_dato: boolean;
  critico: boolean;
}

export interface CamaraGoldenCampo {
  golden: string;
  fijado_a_mano: boolean;
  cumplen: number;
  fuera: number;
  sin_dato: number;
  reparto: Record<string, number>;
  critico: boolean;
}

export interface CamaraConfigFila {
  device_id: string;
  nombre: string | null;
  ip: string;
  agencia: string | null;
  modelo: string | null;
  rol: string;
  grupo: string;
  capturado_en: string | null;
  cumple: boolean;
  desvios: CamaraDesvio[];
  criticos: number;
}

export interface CamarasConfigAgencia {
  nombre: string;
  cumplen: number;
  fuera: number;
  sin_leer: number;
  criticos: number;
}

export interface BiometricosResponse {
  _status: "ok" | "sin_configurar";
  mensaje?: string;
  totales?: {
    total: number;
    leidos: number;
    cobertura_pct: number;
    cumplen: number;
    revirtieron: number;
    fuera: number;
    criticos: number;
    sin_leer: number;
    no_responden: number;
  };
  agencias?: {
    nombre: string;
    biometricos: number;
    cumplen: number;
    fuera: number;
    sin_leer: number;
    no_responden: number;
    criticos: number;
  }[];
  generated_at?: string;
}

export interface CamarasConfigResponse {
  _status: "ok" | "sin_configurar";
  mensaje?: string;
  totales?: {
    // Nombres homologados con las demás pantallas (barra ResumenCobertura).
    total: number;
    leidos: number;
    cobertura_pct: number;
    cumplen: number;
    revirtieron: number;
    fuera: number;
    criticos: number;
    sin_leer: number;
    no_responden: number;
    // Se conservan por compatibilidad con lo que ya los leía.
    camaras: number;
    leidas: number;
  };
  golden?: Record<string, Record<string, CamaraGoldenCampo>>;
  agencias?: CamarasConfigAgencia[];
  camaras?: CamaraConfigFila[];
  sin_leer?: { nombre: string | null; ip: string; agencia: string | null; motivo: string }[];
  generated_at?: string;
}

// ── Inventario de la LAN: que hay detras del router de cada agencia ────────
// Cruza dos cosechas independientes (la LAN que ve el RUT y los equipos que
// administra HikCentral). La categoria que importa es `sin_gestionar`.

export interface InventarioLanHost {
  ip: string;
  mac: string | null;
  estado_arp: string | null;
  nombre_dhcp: string | null;
  clase: "gestionado" | "sin_gestionar" | "fantasma";
  nombre_hikcentral: string | null;
  tipo: string | null;
  modelo: string | null;
}

export interface InventarioLanAgencia {
  agencia: string | null;
  device_id: string;
  ip: string;
  subred?: string;
  _status: "ok" | "sin_datos";
  motivo?: string;
  capturado_en?: string;
  vivos_en_lan?: number;
  gestionados?: number;
  sin_gestionar_ips?: number;
  sin_gestionar_equipos?: number;
  fantasmas?: number;
  con_nombre_dhcp?: number;
  hosts?: InventarioLanHost[];
}

export interface InventarioLanResponse {
  agencias: InventarioLanAgencia[];
  totales: {
    agencias_con_datos: number;
    agencias_sin_datos: number;
    vivos_en_lan: number;
    gestionados: number;
    sin_gestionar_equipos: number;
    fantasmas: number;
  };
  generated_at: string;
}


// ── Avance en vivo de la recosecha (GET /config/recosechar/estado) ───────────
// El aviso de la pantalla se apagaba con un temporizador ciego de 6 minutos:
// decia "terminada" mientras el servidor iba por el equipo 30. Esto es la
// verdad del servidor.

export interface RecosechaHecho {
  nombre: string;
  estado: string; // "ok" | "sin_datos" | "offline" | "error"
  ms: number | null;
  motivo: string | null;
}

export interface RecosechaEstado {
  hay_run: boolean;
  device_type?: string;
  actor?: string;
  terminado?: boolean;
  inicio?: string;
  fin?: string | null;
  segundos?: number;
  total?: number;
  hechos?: number;
  logrados?: number;
  por_estado?: Record<string, number>;
  bajando?: string[];
  ultimo?: RecosechaHecho | null;
  recientes?: RecosechaHecho[];
  perfiles_activos?: string[];
  // Perfil que el operador pidio explicitamente. Sin el, cada equipo resuelve
  // el suyo con la regla del mas antiguo.
  perfil?: string | null;
}

// ── Grabadores (NVR/DVR) — fase 1: inventario y lectura ────────────────────

export interface GrabadorDesvio {
  campo: string;
  actual: string | null;
  esperado: string;
  // `agencia` = la zona horaria, que depende de dónde está el equipo.
  // `politica` = lo decidió el cliente, la mayoría no vota.
  // `mayoria` = salió del reparto; es un candidato, no una verdad.
  // `fijado_a_mano` = alguien lo impuso para este grupo.
  origen: "agencia" | "politica" | "mayoria" | "fijado_a_mano";
  sin_dato: boolean;
  critico: boolean;
}

export interface GrabadorCampoGolden {
  golden: string;
  origen: "politica" | "mayoria" | "fijado_a_mano";
  critico: boolean;
  cumplen: number;
  fuera: number;
  sin_dato: number;
  reparto: Record<string, number>;
}

export interface GrabadorEstabilidad {
  // `revirtio` es el estado que la pantalla no distinguía: un equipo que se
  // arregló y volvió atrás se veía igual que uno que nunca estuvo bien, y son
  // problemas distintos — al primero no le falta configurarlo, le falta que
  // dejen de reescribírselo.
  estado:
    | "aguanta"
    | "bien_reciente"
    | "revirtio"
    | "nunca_bien"
    | "sin_lectura"
    | "sin_golden";
  lecturas: number;
  cambios: number;
  ultimo_cambio: string | null;
  cambio_ultimo_de: string | null;
  cambio_ultimo_a: string | null;
}

export interface GrabadorFila {
  device_id: string;
  estabilidad: GrabadorEstabilidad;
  // Familia + modelo: los DVR analógicos no comparten golden con los NVR.
  grupo: string | null;
  cumple: boolean;
  desvios: GrabadorDesvio[];
  sin_dato: GrabadorDesvio[];
  criticos: number;
  nombre: string | null;
  ip: string;
  agencia: string | null;
  // Sale de `deviceInfo` en la primera cosecha: HikCentral da modelo de cámara
  // pero NO de grabador, así que hasta entonces es null.
  modelo: string | null;
  // Los DVR analógicos no tienen canales IP y llevarán golden propio (fase 2).
  analogico: boolean;
  leido: boolean;
  motivo: string | null;
  capturado_en: string | null;
  firmware: string | null;
  zona_horaria: string | null;
  modo_hora: string | null;
  ntp: string | null;
  ssh: boolean | null;
  upnp: boolean | null;
  https: boolean | null;
  dhcp: boolean | null;
  hdd: string | null;
  canales: number | null;
  canales_en_linea: number | null;
  canales_caidos: number | null;
  uptime_segundos: number | null;
}

// GET /config/grabadores/compat-axelia: la codificacion que cada grabador
// guarda por canal, contra la plantilla Compat-Axelia.
export interface GrabadorCanalAxelia {
  device_id: string;
  agencia: string;
  grabador: string | null;
  grabador_ip: string | null;
  canal: string;
  nombre: string | null;
  ip: string | null;
  modelo: string | null;
  cumple: boolean;
  desvios: { campo: string; actual: string | number | null; esperado: string }[];
  capturado_en: string | null;
  /** Campos que este canal juzga con su oro propio (decisión de Edgar), no con la plantilla. */
  oro_propio?: string[];
}

export interface GrabadoresAxeliaResponse {
  _status: "ok" | "sin_configurar";
  mensaje?: string;
  plantilla?: Record<string, string>;
  totales?: {
    canales: number;
    cumplen: number;
    fuera: number;
    grabadores_sin_leer: number;
    paneles_omitidos?: number;
  };
  agencias?: { nombre: string; cumplen: number; fuera: number; sin_leer: number }[];
  canales?: GrabadorCanalAxelia[];
  sin_leer?: {
    device_id: string;
    agencia: string;
    nombre: string | null;
    ip: string | null;
    motivo: string;
  }[];
}

export interface GrabadoresResponse {
  _status: "ok" | "sin_configurar";
  mensaje?: string;
  generado_en?: string;
  totales?: {
    grabadores: number;
    total: number;
    leidos: number;
    sin_leer: number;
    no_responden: number;
    analogicos: number;
    // Va primero en la pantalla: un 100 % de cumplimiento sobre un tercio de la
    // flota no es un 100 %.
    cobertura_pct: number;
    revirtieron: number;
    aguantan: number;
    cumplen: number;
    fuera: number;
    criticos: number;
    canales: number;
    canales_caidos: number;
  };
  golden?: Record<string, Record<string, GrabadorCampoGolden>>;
  modelos?: { modelo: string; equipos: number }[];
  agencias?: {
    nombre: string;
    grabadores: number;
    cumplen: number;
    fuera: number;
    sin_leer: number;
    criticos: number;
    canales_caidos: number;
  }[];
  grabadores?: GrabadorFila[];
}

// ── Paneles de control de acceso — inventario, lectura y golden ────────────
//
// Son los equipos que abren las puertas peatonales y las plumas vehiculares.
// Hasta la migración 076 estaban dados de alta como biométricos, que es por lo
// que no tenían pantalla: el detector de deriva de biométricos no le encuentra
// un solo campo a un panel.

export interface PanelPuerta {
  numero: number;
  nombre: string | null;
  tiempo_apertura: number | null;
  tiempo_max_abierta: number | null;
  alarma_magnetico: number | null;
  // `alwaysClose` / `alwaysOpen`. Se muestran pero NO alarman: un panel
  // vehicular con el contacto al revés puede ser una pluma montada distinto.
  tipo_magnetico: string | null;
  tipo_boton: string | null;
}

export interface PanelFila {
  device_id: string;
  nombre: string | null;
  ip: string;
  agencia: string | null;
  // Peatonal o vehicular: llevan golden separado porque los tiempos de una
  // puerta de personas y los de una pluma de coches no tienen por qué coincidir.
  familia: string;
  modelo: string | null;
  leido: boolean;
  motivo: string | null;
  capturado_en: string | null;
  grupo: string | null;
  estabilidad: GrabadorEstabilidad;
  cumple: boolean;
  desvios: GrabadorDesvio[];
  sin_dato: GrabadorDesvio[];
  criticos: number;
  firmware: string | null;
  zona_horaria: string | null;
  modo_hora: string | null;
  ntp: string | null;
  dhcp: boolean | null;
  // Sin huso horario: el panel la manda como `2026-09-08T22:18:29`. Se muestra
  // tal cual y no se opera con ella.
  hora_local: string | null;
  puertas: number | null;
  puertas_detalle: PanelPuerta[];
}

export interface PanelesResponse {
  _status: "ok" | "sin_configurar";
  mensaje?: string;
  generado_en?: string;
  totales?: {
    paneles: number;
    total: number;
    leidos: number;
    sin_leer: number;
    no_responden: number;
    cobertura_pct: number;
    revirtieron: number;
    aguantan: number;
    cumplen: number;
    fuera: number;
    criticos: number;
    puertas: number;
  };
  golden?: Record<string, Record<string, GrabadorCampoGolden>>;
  familias?: { familia: string; equipos: number }[];
  agencias?: {
    nombre: string;
    paneles: number;
    cumplen: number;
    fuera: number;
    sin_leer: number;
    criticos: number;
    puertas: number;
  }[];
  paneles?: PanelFila[];
}
